'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

const initialState: Record<string, string> = {};

enum ActionType {
  SUSPENSION_STARTED,
  SUSPENSION_FINSHED,
}

type Action = {
  type: ActionType;
  data: string;
};

type SuspensionsContextType = {
  createSuspension: (handle: string) => void;
  removeSuspension: (handle: string) => void;
  suspended: (handle: string) => string | null;
};

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case ActionType.SUSPENSION_STARTED: {
      return { ...state, [action.data]: 'STARTED' };
    }

    case ActionType.SUSPENSION_FINSHED: {
      return { ...state, [action.data]: 'FINISHED' };
    }
  }
};

const SuspensionsContext = createContext<SuspensionsContextType | null>(null);

interface Props {
  children: ReactNode;
}

const SuspensionsProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const createSuspension = useCallback((handle: string) => {
    dispatch({ type: ActionType.SUSPENSION_STARTED, data: handle });
  }, []);

  const removeSuspension = useCallback((handle: string) => {
    dispatch({ type: ActionType.SUSPENSION_FINSHED, data: handle });
  }, []);

  const suspended = useCallback(
    (handle: string) => state[handle] || null,
    [state]
  );

  const value = useMemo(
    () => ({ suspended, createSuspension, removeSuspension }),
    [suspended, createSuspension, removeSuspension]
  );

  return (
    <SuspensionsContext.Provider value={value}>
      {children}
    </SuspensionsContext.Provider>
  );
};

function useSuspensions() {
  const context = useContext(SuspensionsContext);
  if (!context) throw new Error('useSuspensions requires a context!');

  return context;
}

export { SuspensionsProvider, useSuspensions };
